import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, number } from 'yup';
import PropTypes from 'prop-types';
import { Button, InputAdornment, Slider, CircularProgress } from '@mui/material';
import { SelectInput, TextInput } from '../../../Helpers/Inputs';
import NumberFormatDollars from '../../../Helpers/Formatters/NumberFormatDollars';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import NoQuoteAvailable from '../../Common/SubComponents/NoQuoteAvailable';
import { CollapsibleBasicInfoSummary } from '../../Common/SubComponents/BasicInfoSummary';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined';
import QuoteCard from '../../Common/Cards/QuoteCard';
import AnnualIncomeDialog from '../Helpers/AnnualIncomeDialog';
import ProductDetails from '../Helpers/ProductDetails';
import ProductInfoDialog from '../Helpers/ProductInfoDialog';
import WhatIsThisDialog from '../Helpers/WhatIsThisDialog';
import QuoteFor from '../../Common/QuoteFor/QuoteFor';

export default function Quoter(props) {

  const formMethods = useForm({
    resolver: yupResolver(Quoter.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState
  });

  const handleAnnualIncomeChange = event => {
    const val = event.target.value.replace(/\D/, '');
    setAnnualIncome(val);
  }

  const [loading, setLoading] = useState(true);
  const [productInfoDialogOpen, setProductInfoDialogOpen] = useState(false);
  const [aboutAnnualIncomeOpen, setAboutAnnualIncomeOpen] = useState(false);
  const [whatIsThisOpen, setWhatIsThisOpen] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [manualDuties, setManualDuties] = useState(100);
  const [annualIncome, setAnnualIncome] = useState('45000');

  const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

  const runQuote = (getIsSubscribed) => {
    const formattedDate = props.basicInfo.birthdate.replace('/', '-');

    fetch(
      `${covrServicesUrl}/api/Ameritas?Birthdate=${formattedDate}&StateCode=${props.basicInfo.state}&Gender=${props.basicInfo.gender}&Face=${props.formState.coverageAmount}&Tobacco=${props.basicInfo.tobacco}&OccupationClass=${manualDuties}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (getIsSubscribed() === false) {
          return;
        }
        setQuoteResults(data);
        setLoading(false);
      })
      .catch((error) => {
        if (getIsSubscribed()) {
          ErrorHandler(error, setQuoteResults);
          setLoading(false);
        }
      });
  };

  let debounce = null;
  useEffect(() => {
    setLoading(true);

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    };
  }, [props.formState.coverageAmount, manualDuties]);

  const coverageAmounts = () => {
    return [
      { value: 25000, label: '$25K' },
      { value: 30000, label: '' },
      { value: 35000, label: '' },
      { value: 40000, label: '' },
      { value: 45000, label: '' },
      { value: 50000, label: '' },
      { value: 55000, label: '' },
      { value: 60000, label: '' },
      { value: 65000, label: '' },
      { value: 70000, label: '' },
      { value: 75000, label: '' },
      { value: 80000, label: '' },
      { value: 85000, label: '' },
      { value: 90000, label: '' },
      { value: 95000, label: '' },
      { value: 100000, label: '$100K' },
    ];
  };

  return (
    <Container>
      <Row className="quoter">
        <Col lg={4} className="p-3 mb-3">
          <FormProvider {...formMethods}>
            <Form>
              <h2>Get your quote</h2>
              <div className='mb-3'>
                <TextInput
                  name="coverageAmount"
                  id="coverageAmount"
                  label="Benefit Amount"
                  value={props.formState.coverageAmount}
                  onChange={(e) => props.onFieldChange('coverageAmount', Number(e.target.value))}
                  variant="outlined"
                  InputProps={{
                    inputComponent: NumberFormatDollars,
                    className: 'coverage-input',
                  }}
                />
              </div>
              <div className="ms-3 me-4 mb-3 coverageAmount-slider">
                <Slider
                  min={25000}
                  max={100000}
                  step={null}
                  value={Number(props.formState.coverageAmount)}
                  onChange={(e, v) => props.onFieldChange('coverageAmount', v)}
                  className="m-2"
                  marks={coverageAmounts()}
                />
              </div>
              <div className='mb-3'>
                <TextInput
                  type="number"
                  name="annualIncome"
                  id="annualIncome"
                  label="Annual Income"
                  value={annualIncome}
                  onChange={handleAnnualIncomeChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </div>
              <div className='mb-3'>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => {
                    setAboutAnnualIncomeOpen(true);
                  }}
                >
                  ABOUT ANNUAL INCOME
                </Button>

                <AnnualIncomeDialog
                  open={aboutAnnualIncomeOpen}
                  onAnnualIncomeDialogClosed={() => {
                    setAboutAnnualIncomeOpen(false);
                  }}
                />
              </div>
              <div className='mb-3'>
                <SelectInput
                  name="manualDuties"
                  id="manualDuties"
                  value={manualDuties}
                  onChange={(e) => setManualDuties(e.target.value)}
                  label="Manual Duties"
                  selectOptions={[
                    { value: '0', label: '0%' },
                    { value: '10', label: '10%' },
                    { value: '20', label: '20%' },
                    { value: '30', label: '30%' },
                    { value: '40', label: '40%' },
                    { value: '50', label: '50%' },
                    { value: '60', label: '60%' },
                    { value: '70', label: '70%' },
                    { value: '80', label: '80%' },
                    { value: '90', label: '90%' },
                    { value: '100', label: '100%' },
                  ]}
                />
              </div>
              <div className='mb-3'>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => {
                    setWhatIsThisOpen(true);
                  }}
                >
                  What is this?
                </Button>

                <WhatIsThisDialog
                  open={whatIsThisOpen}
                  onWhatIsThisDialogClosed={() => {
                    setWhatIsThisOpen(false);
                  }}
                />
              </div>

              <CollapsibleBasicInfoSummary
                birthdate={props.basicInfo.birthdate}
                tobacco={props.basicInfo.tobacco}
                height={props.basicInfo.height}
                weight={props.basicInfo.weight}
                state={props.basicInfo.state}
                gender={props.basicInfo.gender}
                showEditLink={true}
                editLinkOnClick={props.onBack}
              />

            </Form>
          </FormProvider>
        </Col>

        <Col lg={8} className="pt-3">
          <QuoteResult
            coverageAmount={props.formState.coverageAmount}
            result={quoteResults}
            loading={loading}
            setProductInfoDialogOpen={setProductInfoDialogOpen}
            onNext={props.onNext}
          />
        </Col>
      </Row>

      <ProductInfoDialog
        open={productInfoDialogOpen}
        onProductInfoDialogClosed={() => {
          setProductInfoDialogOpen(false);
        }}
      />
    </Container>
  );
}

function QuoteResult(props) {
  const [expanded, setExpanded] = useState(false);

  if (props.loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
      </div>
    );
  }

  if (!props.result.MonthlyPremium) {
    return <NoQuoteAvailable />;
  }

  return (
    <div className="quote-result-section">
      <QuoteFor coverageAmount={props.coverageAmount} productType="Whole Life" />

      <QuoteCard
        carrierName="Ameritas"
        productName="Dynamic Fundamental®"
        productNameSubtitle="Disability Income Insurance"
        monthlyRate={props.result.MonthlyPremium}
        logoUrl="/logos/ameritas.png"
        rating="A+"
        productDetails={<ProductDetails setProductInfoDialogOpen={props.setProductInfoDialogOpen} />}
        expanded={expanded}
        expand={() => setExpanded(!expanded)}
        onClick={props.onNext}
      />
    </div>
  );
}

Quoter.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  queryString: PropTypes.object,
};

Quoter.ValidationSchema = object().shape({
  annualIncome: number()
    .transform(emptyStringToUndefined)
    .typeError('Annual Income must be a number')
    .required('Annual Income is required')
    .min(18000, 'Annual Income must be $18,000 or greater'),
})

Quoter.DefaultFormState = (prefill = false) => {
  return {
    coverageAmount: 50000,
    manualDuties: 100,
    annualIncome: 45000,
  };
};
